import {
  Badge,
  Box,
  Input,
  InputGroup,
  InputRightAddon,
  useColorMode,
} from '@chakra-ui/react';
import { first } from 'lodash';
import * as math from 'mathjs';
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { calculateSegmentLength } from '../../../domain/geometry/algorithms/util/line-segment';
import { LineSegment } from '../../../domain/geometry/geometric-types';
import { useViewer } from '../../common/ForgeViewer';
import { projectInDOM, useCameraChangedEvent } from './util';

type EditableLengthLabelProps = {
  segment: LineSegment;
  viewer: Autodesk.Viewing.Viewer3D;
  isEditable?: boolean;
  isDragging?: boolean;
  onLengthSubmit?: (newLengthInMeters: number) => void;
};

export function EditableLengthLabel({
  segment,
  viewer,
  isEditable = false,
  isDragging = false,
  onLengthSubmit,
}: EditableLengthLabelProps) {
  const [cameraPosition, setCameraPosition] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';

  const { loadedModels } = useViewer();
  const unitScale = useMemo(
    () => first(Object.values(loadedModels))?.getUnitScale() ?? 1,
    [loadedModels]
  );

  const lengthInMeters = useMemo(
    () => calculateSegmentLength(segment) * unitScale,
    [segment, unitScale]
  );

  const positions2D = useMemo(
    () =>
      [projectInDOM(segment[0], viewer), projectInDOM(segment[1], viewer)] as [
        [number, number],
        [number, number]
      ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cameraPosition, segment, viewer]
  );

  useCameraChangedEvent(
    viewer,
    useCallback(() => {
      setCameraPosition({});
    }, [])
  );

  // Handle global keyboard events when editable and not already editing
  useEffect(() => {
    if (!isEditable || isEditing) return;

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Don't capture if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Check if key is a digit or decimal point
      if (/^[0-9.,]$/.test(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        setInputValue(e.key === ',' ? '.' : e.key);
      }
    };

    // Use capture phase to get events before the viewer
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isEditable, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const parsedValue = parseFloat(inputValue);
      if (!isNaN(parsedValue) && parsedValue > 0 && onLengthSubmit) {
        // Convert from display units (meters) back to internal units
        onLengthSubmit(parsedValue);
      }
      setIsEditing(false);
      setInputValue('');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setInputValue('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(',', '.');
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    setInputValue('');
  };

  const midpoint = {
    top: positions2D[0][1] - 0.5 * (positions2D[0][1] - positions2D[1][1]),
    left: positions2D[0][0] - 0.5 * (positions2D[0][0] - positions2D[1][0]),
  };

  const isBlue = isEditable && !isDragging;

  return (
    <Box
      position="absolute"
      zIndex={15}
      style={midpoint}
      pointerEvents={isEditing ? 'auto' : 'none'}
      transform="translate(-50%, -50%)"
    >
      {isEditing ? (
        <InputGroup size="xs">
          <Input
            ref={inputRef}
            width="45px"
            height="18px"
            minHeight="18px"
            textAlign="center"
            backgroundColor="white"
            borderColor="blue.400"
            borderRadius="sm"
            borderRightRadius={0}
            fontSize="2xs"
            px={1}
            py={0}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            autoFocus
          />
          <InputRightAddon
            height="18px"
            minHeight="18px"
            fontSize="2xs"
            px={1}
            borderRadius="sm"
            borderLeftRadius={0}
          >
            m
          </InputRightAddon>
        </InputGroup>
      ) : (
        <Badge
          textTransform="none"
          fontSize="2xs"
          px={1}
          py={0}
          borderRadius="sm"
          variant={isBlue && !isDarkMode ? 'outline' : 'solid'}
          colorScheme={isBlue ? 'blue' : 'gray'}
          backgroundColor={isBlue && !isDarkMode ? 'white' : undefined}
        >
          {math.round(lengthInMeters, 2)} m
        </Badge>
      )}
    </Box>
  );
}
